import {
  CUT_STRATEGIES,
  orderOversizeAllowed,
  STRIP_MODE_HELP,
  strategyUsesOffcutDisplay,
} from "@plaatplan/core";
import {
  resolveCoreMessage,
  resolveCutStrategyDescription,
  resolveCutStrategyLabel,
} from "@plaatplan/i18n";
import { useLocale } from "../i18n/useLocale";
import { LengthInput } from "../input/LengthInput";

function StrategyStatusNotices({
  werkplan,
  stripEligibleSheetCount,
  totalSheetCount,
  strategyOverrideCount,
}) {
  const { display } = useLocale();
  const stripsGloballyAvailable = stripEligibleSheetCount > 0;
  const stripsSelected = werkplan.globalStrategy === "strips";
  const stripsPartiallyAvailable = stripsGloballyAvailable
    && totalSheetCount > 1
    && stripEligibleSheetCount < totalSheetCount;

  const notices = [];

  if (strategyOverrideCount > 0 && totalSheetCount > 0) {
    notices.push({
      key: "overrides",
      tone: "note",
      content: display.t(
        totalSheetCount === 1
          ? "web.werkplan.sawSettings.strategy.overridesSingular"
          : "web.werkplan.sawSettings.strategy.overridesPlural",
        {
          overrideCount: strategyOverrideCount,
          totalCount: totalSheetCount,
        },
      ),
    });
  }

  if (!stripsGloballyAvailable && totalSheetCount > 0) {
    notices.push({
      key: "strips-unavailable",
      tone: "muted",
      content: display.t("web.werkplan.sawSettings.strategy.stripsUnavailable"),
    });
  }

  if (stripsSelected && !stripsGloballyAvailable && totalSheetCount > 0) {
    notices.push({
      key: "strips-fallback",
      tone: "note",
      content: display.t("web.werkplan.sawSettings.strategy.stripsFallback"),
    });
  }

  if (stripsPartiallyAvailable) {
    notices.push({
      key: "strips-partial",
      tone: "muted",
      content: display.t("web.werkplan.sawSettings.strategy.stripsPartial", {
        eligibleCount: stripEligibleSheetCount,
        totalCount: totalSheetCount,
      }),
    });
  }

  if (stripsSelected && stripsGloballyAvailable) {
    notices.push({
      key: "strips-active",
      tone: "muted",
      content: display.t("web.werkplan.sawSettings.strategy.stripsActive"),
    });
  }

  if (notices.length === 0) return null;

  return (
    <div
      className="export-settings-notices export-settings-card-status"
      aria-label={display.t("web.werkplan.sawSettings.strategy.statusLabel")}
    >
      {notices.map((notice) => (
        <p
          key={notice.key}
          className={`export-settings-banner export-settings-banner-${notice.tone}`}
          role="status"
        >
          {notice.content}
        </p>
      ))}
    </div>
  );
}

function WerkplanStrategySettings({
  werkplan,
  onChange,
  stripEligibleSheetCount,
  totalSheetCount,
  strategyOverrideCount,
}) {
  const { display } = useLocale();
  const update = (field, value) => onChange({ ...werkplan, [field]: value });
  const updateGlobalStrategy = (globalStrategy) => {
    const next = { ...werkplan, globalStrategy };
    if (strategyUsesOffcutDisplay(globalStrategy) && !werkplan.showOffcuts) {
      next.showOffcuts = true;
    }
    if (globalStrategy === "strips") {
      next.orderOversizeEnabled = false;
    }
    onChange(next);
  };
  const stripsGloballyAvailable = stripEligibleSheetCount > 0;
  const stripsSelected = werkplan.globalStrategy === "strips";

  return (
    <div className="export-settings-card">
      <h3>{display.t("web.werkplan.sawSettings.strategy.title")}</h3>
      <div className="export-settings-grid">
        <label>
          <span>{display.t("web.werkplan.sawSettings.strategy.label")}</span>
          <select
            value={werkplan.globalStrategy}
            onChange={(event) => updateGlobalStrategy(event.target.value)}
          >
            {Object.entries(CUT_STRATEGIES).map(([value]) => (
              <option
                key={value}
                value={value}
                disabled={value === "strips" && !stripsGloballyAvailable}
              >
                {resolveCutStrategyLabel(display, value)}
                {value === "strips" && !stripsGloballyAvailable
                  ? display.t("web.werkplan.sawSettings.strategy.notPossible")
                  : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="export-checkbox">
          <input
            type="checkbox"
            checked={werkplan.emphasizeExactSizes}
            onChange={(event) => update("emphasizeExactSizes", event.target.checked)}
          />
          <span>{display.t("web.werkplan.sawSettings.strategy.emphasizeExactSizes")}</span>
        </label>
        <label className="export-checkbox">
          <input
            type="checkbox"
            checked={werkplan.showCutSequence}
            onChange={(event) => update("showCutSequence", event.target.checked)}
          />
          <span>{display.t("web.werkplan.sawSettings.strategy.showCutSequence")}</span>
        </label>
      </div>
      <p className="strategy-description">
        {resolveCutStrategyDescription(display, werkplan.globalStrategy)}
      </p>

      <StrategyStatusNotices
        werkplan={werkplan}
        stripEligibleSheetCount={stripEligibleSheetCount}
        totalSheetCount={totalSheetCount}
        strategyOverrideCount={strategyOverrideCount}
      />

      {stripsGloballyAvailable && (
        <details className="strip-mode-details" open={stripsSelected}>
          <summary>{display.t("web.werkplan.sawSettings.strategy.stripDetailsSummary")}</summary>
          <div className="strip-mode-details-body">
            <p>{resolveCoreMessage(display, STRIP_MODE_HELP.whenToUse.body)}</p>
            <p className="export-settings-hint">{resolveCoreMessage(display, STRIP_MODE_HELP.whenToUse.example)}</p>
          </div>
        </details>
      )}
    </div>
  );
}

function WerkplanOffcutSettings({ werkplan, onChange, offcutDisplayRelevant }) {
  const { display } = useLocale();
  const update = (field, value) => onChange({ ...werkplan, [field]: value });

  return (
    <div className="export-settings-card export-settings-card-offcut">
      <h3>{display.t("web.werkplan.sawSettings.offcuts.title")}</h3>
      <p className="export-settings-hint">
        {display.t("web.werkplan.sawSettings.offcuts.hint")}
      </p>
      {!offcutDisplayRelevant && (
        <p className="export-settings-hint export-settings-inactive-hint">
          {display.t("web.werkplan.sawSettings.offcuts.inactiveHint")}
        </p>
      )}
      <div className="export-settings-grid">
        <label>
          <span>{display.t("web.werkplan.sawSettings.offcuts.minLength")}</span>
          <LengthInput
            valueMm={werkplan.minOffcutLength}
            minMm={1}
            onChangeMm={(value) => update("minOffcutLength", value)}
          />
        </label>
        <label>
          <span>{display.t("web.werkplan.sawSettings.offcuts.minWidth")}</span>
          <LengthInput
            valueMm={werkplan.minOffcutWidth}
            minMm={1}
            onChangeMm={(value) => update("minOffcutWidth", value)}
          />
        </label>
        <label className={`export-checkbox${offcutDisplayRelevant ? "" : " export-checkbox-muted"}`}>
          <input
            type="checkbox"
            checked={werkplan.showOffcuts}
            onChange={(event) => update("showOffcuts", event.target.checked)}
          />
          <span>{display.t("web.werkplan.sawSettings.offcuts.showInPlan")}</span>
        </label>
      </div>
    </div>
  );
}

function WerkplanOrderSizeSettings({ werkplan, onChange }) {
  const { display } = useLocale();
  const update = (field, value) => onChange({ ...werkplan, [field]: value });
  const oversizeAllowed = orderOversizeAllowed(werkplan);

  return (
    <div className="export-settings-card">
      <h3>{display.t("web.werkplan.sawSettings.orderSize.title")}</h3>
      <p className="export-settings-hint">
        {display.t("web.werkplan.sawSettings.orderSize.hint")}
      </p>
      {!oversizeAllowed && (
        <p className="export-settings-hint export-settings-inactive-hint">
          {display.t("web.werkplan.sawSettings.orderSize.inactiveHint")}
        </p>
      )}
      <label className={`export-checkbox${oversizeAllowed ? "" : " export-checkbox-muted"}`}>
        <input
          type="checkbox"
          checked={werkplan.orderOversizeEnabled}
          disabled={!oversizeAllowed}
          onChange={(event) => update("orderOversizeEnabled", event.target.checked)}
        />
        <span>{display.t("web.werkplan.sawSettings.orderSize.enable")}</span>
      </label>
      {werkplan.orderOversizeEnabled && oversizeAllowed && (
        <div className="export-settings-grid order-oversize-grid">
          <label>
            <span>{display.t("web.werkplan.sawSettings.orderSize.extraLength")}</span>
            <LengthInput
              valueMm={werkplan.orderOversizeLength}
              minMm={0}
              onChangeMm={(value) => update("orderOversizeLength", value)}
            />
          </label>
          <label>
            <span>{display.t("web.werkplan.sawSettings.orderSize.extraWidth")}</span>
            <LengthInput
              valueMm={werkplan.orderOversizeWidth}
              minMm={0}
              onChangeMm={(value) => update("orderOversizeWidth", value)}
            />
          </label>
        </div>
      )}
    </div>
  );
}

export function WerkplanSawSettings({
  werkplan,
  onChange,
  stripEligibleSheetCount = 0,
  totalSheetCount = 0,
  strategyOverrideCount = 0,
  offcutDisplayRelevant = false,
}) {
  const { display } = useLocale();

  return (
    <section className="export-section no-print">
      <h2>{display.t("web.werkplan.sawSettings.title")}</h2>
      <p className="export-note">
        {display.t("web.werkplan.sawSettings.intro")}
      </p>

      <div className="export-settings-stack">
        <div className="export-settings-row">
          <WerkplanStrategySettings
            werkplan={werkplan}
            onChange={onChange}
            stripEligibleSheetCount={stripEligibleSheetCount}
            totalSheetCount={totalSheetCount}
            strategyOverrideCount={strategyOverrideCount}
          />

          <WerkplanOffcutSettings
            werkplan={werkplan}
            onChange={onChange}
            offcutDisplayRelevant={offcutDisplayRelevant}
          />
        </div>

        <WerkplanOrderSizeSettings werkplan={werkplan} onChange={onChange} />

        <div className="export-settings-card">
          <h3>{display.t("web.werkplan.sawSettings.generalNote.title")}</h3>
          <p className="export-settings-hint">
            {display.t("web.werkplan.sawSettings.generalNote.hint")}
          </p>
          <label className="export-wide-field">
            <span className="visually-hidden">
              {display.t("web.werkplan.sawSettings.generalNote.label")}
            </span>
            <textarea
              className={werkplan.generalNote.trim() ? "" : "empty-print-field"}
              value={werkplan.generalNote}
              onChange={(event) => onChange({ ...werkplan, generalNote: event.target.value })}
              rows={3}
              placeholder={display.t("web.werkplan.sawSettings.generalNote.placeholder")}
            />
          </label>
        </div>
      </div>
    </section>
  );
}
